import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import AIGlowEffect from "../AIGlowEffect/AIGlowEffect";
import { shadows } from "../theme/shadows";

/**
 * AI Summary Card Component
 * Displays AI-generated summaries with animated glow effect
 */

const AISummaryCard = ({
  summary,
  loading = false,
  error = null,
  alertsCount = 0,
  systemsCount = 0,
  onRetry,
}) => {
  const [textVisible, setTextVisible] = useState(false);

  useEffect(() => {
    if (summary && !loading) {
      // Delay text fade-in slightly for better UX
      const timer = setTimeout(() => setTextVisible(true), 200);
      return () => clearTimeout(timer);
    } else {
      setTextVisible(false);
    }
  }, [summary, loading]);

  return (
    <AIGlowEffect width={0} blur={2} animationSpeed={0.5} intervalSpeed={0.25}>
      <CardContainer>
        <CardHeader>
          <HeaderText>
            <Title>AI Insights</Title>
            {systemsCount > 0 && (
              <Subtitle>
                Monitoring {systemsCount} system{systemsCount !== 1 ? "s" : ""}
                {alertsCount > 0 &&
                  ` • ${alertsCount} alert${alertsCount !== 1 ? "s" : ""}`}
              </Subtitle>
            )}
          </HeaderText>
        </CardHeader>

        <SummaryContent>
          {loading ? (
            <LoadingState>
              <LoadingSpinner />
              <LoadingText>Generating AI summary...</LoadingText>
            </LoadingState>
          ) : error ? (
            <ErrorState>
              <ErrorIcon>⚠️</ErrorIcon>
              <ErrorText>{error}</ErrorText>
              {onRetry && (
                <RetryButton onClick={onRetry}>Try Again</RetryButton>
              )}
            </ErrorState>
          ) : summary ? (
            <SummaryText visible={textVisible}>{summary}</SummaryText>
          ) : (
            <PlaceholderText>
              AI summary will appear here once generated
            </PlaceholderText>
          )}
        </SummaryContent>

        <CardFooter>
          <FooterBadge>
            <SparkleIcon>✨</SparkleIcon>
            <FooterText>Powered by AI</FooterText>
          </FooterBadge>
        </CardFooter>
      </CardContainer>
    </AIGlowEffect>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const CardContainer = styled.div`
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.75) 0%,
    rgba(248, 249, 251, 0.85) 100%
  );
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  border-radius: 25px;
  padding: 24px;
  position: relative;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(188, 130, 243, 0.5) 20%,
      rgba(141, 159, 255, 0.5) 50%,
      rgba(245, 185, 234, 0.5) 80%,
      transparent
    );
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 25px;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(188, 130, 243, 0.03),
      rgba(141, 159, 255, 0.03),
      transparent 70%
    );
    pointer-events: none;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const HeaderText = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  background: linear-gradient(135deg, #bc82f3, #8d9fff, #aa6eee);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.3;
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 2px;
  font-weight: 500;
`;

const SummaryContent = styled.div`
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const SummaryText = styled.div`
  font-size: 14px;
  line-height: 1.7;
  color: rgba(0, 0, 0, 0.8);
  white-space: pre-wrap;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transform: translateY(${(props) => (props.visible ? 0 : "10px")});
  transition:
    opacity 0.6s ease-out,
    transform 0.6s ease-out;

  strong {
    color: rgba(0, 0, 0, 0.9);
    font-weight: 600;
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px 0;
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid rgba(188, 130, 243, 0.2);
  border-top-color: #bc82f3;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.div`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
  font-weight: 500;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 0;
`;

const ErrorIcon = styled.div`
  font-size: 32px;
  line-height: 1;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: rgba(231, 76, 60, 0.8);
  font-weight: 500;
  text-align: center;
`;

const RetryButton = styled.button`
  margin-top: 8px;
  padding: 8px 16px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(
    135deg,
    rgba(188, 130, 243, 0.15),
    rgba(141, 159, 255, 0.15)
  );
  color: #8d9fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(188, 130, 243, 0.25),
      rgba(141, 159, 255, 0.25)
    );
    transform: translateY(-2px);
  }
`;

const PlaceholderText = styled.div`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.4);
  font-style: italic;
  text-align: center;
  padding: 20px 0;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const FooterBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(188, 130, 243, 0.1),
    rgba(141, 159, 255, 0.1)
  );
`;

const SparkleIcon = styled.span`
  font-size: 14px;
  line-height: 1;
`;

const FooterText = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export default AISummaryCard;
